
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// validations
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'

// composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import type { SettingsWabaTemplate, NotificationItTemplateT, AttachWabaTemplatesPayload } from '@/core/types/settings.types'

// realisations
import { settings } from '@/core/realisations/settings.realisation'

export default defineComponent({
    components: { Modal, Loader, TheButton, TheSelect },
    props: {
        id: {
            type: Number,
            default: null,
        },
    },
    emits: ['updated', 'cancel'],
    setup(props, { emit }) {
        const { havePermissions } = useAccount()
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_updating: false,
            is_waba_loading: false,
            templates: [] as NotificationItTemplateT[],
            templates_copy: [] as NotificationItTemplateT[],
            waba_templates: [] as number[]
        })

        const data_state = reactive({
            templates: new DataState<SettingsWabaTemplate>({}),
        })

        const state_form$ = useVuelidate(
            {
                templates: {
                    $each: helpers.forEach({
                        body: { required },
                    }),
                },
            },
            state
        )

        const closeModal = () => {
            if (state.is_loading) return
            emit('cancel')
        }

        const loadTemplates = async () => {
            try {
                data_state.templates.is_loading = true

                const response = await settings.getWabaTemplates({
                    page: 1,
                    limit: 9999
                })

                data_state.templates.list = response.data

                data_state.templates.is_failed = false
            } catch (error) {
                data_state.templates.failure.catchError(error)
                data_state.templates.is_failed = true
            } finally {
                data_state.templates.is_loading = false
            }
        }

        const updateNotification = async (id: number, index: number) => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_updating = true

                let body = state.templates[index].body
                await settings.editTemplateNotificationIt(id, body)

                notifications.notify({ title: 'Шаблон успешно отредактирован!', type: 'success' })
                emit('updated')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При редактировании шаблона произошла ошибка',
                })
            } finally {
                state.is_updating = false
            }
        }

        const updateWabaTemplates = async () => {
            try {
                state.is_waba_loading = true

                const payload: AttachWabaTemplatesPayload = {
                    templateIds: state.waba_templates
                }

                await settings.attachWabaTemplates(props.id, payload)
                emit('updated')

                notifications.notify({ type: 'success', title: 'Шаблоны WABA прикреплены' })
            } catch (error) {
                console.error(error)

                notifications.notifyError({
                    error,
                    title: 'При прикреплении шаблонов WABA произошла ошибка',
                })
            } finally {
                state.is_waba_loading = false
            }
        }

        const splitText = (text: string) => {
            const regex = /{{[^}]+}}/g
            let result
            let lastIndex = 0
            const fragments = []

            while ((result = regex.exec(text)) !== null) {
                const index = result.index
                const match = result[0]

                if (index > lastIndex) {
                    fragments.push({ text: text.slice(lastIndex, index), editable: true })
                }
                fragments.push({ text: match, editable: false })
                lastIndex = regex.lastIndex
            }

            if (lastIndex < text.length) {
                fragments.push({ text: text.slice(lastIndex), editable: true })
            }

            return fragments
        }

        const renderText = (index: number, target?: HTMLInputElement) => {
            const editor = document.getElementById(`editor-${index}`)

            if (!editor) return ''

            let fragments = []
            if (target) {
                const spans = target.childNodes
                let text = ''
                spans.forEach((span) => {
                    text += span.textContent
                })
                fragments = splitText(text)
                state.templates[index].body = text
            } else {
                fragments = splitText(state.templates_copy[index].body)
            }
            editor.innerHTML = ''
            const fragmentsOld = splitText(state.templates_copy[index].body)

            fragments.forEach((fragment, i) => {
                const span = document.createElement('span')

                if (!fragment.editable) {
                    span.style.color = 'gray'
                    span.style.backgroundColor = 'lightgray'
                    span.style.pointerEvents = 'none'
                    span.style.userSelect = 'none'
                    span.classList.add('variable')
                    span.textContent = fragmentsOld[i].text
                } else {
                    span.textContent = fragment.text
                }

                editor.appendChild(span)
            })
        }

        const saveCursorPosition = (editor: HTMLElement) => {
            const selection = window.getSelection()
            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0)
                const preCaretRange = range.cloneRange()
                preCaretRange.selectNodeContents(editor)
                preCaretRange.setEnd(range.startContainer, range.startOffset)
                const start = preCaretRange.toString().length

                return { start }
            }
            return null
        }

        const restoreCursorPosition = (editor: HTMLElement, savedPosition: { start: number }) => {
            const selection = window.getSelection()
            if (!selection) return

            const range = document.createRange()
            range.setStart(editor, 0)
            range.collapse(true)

            let charCount = 0
            const nodeStack: Array<Node> = [editor]
            let node: Node | undefined

            while ((node = nodeStack.pop())) {
                if (node.nodeType === Node.TEXT_NODE) {
                    const textLength = node.textContent?.length || 0
                    if (charCount + textLength >= savedPosition.start) {
                        range.setStart(node, savedPosition.start - charCount)
                        break
                    } else {
                        charCount += textLength
                    }
                } else {
                    let i = node.childNodes.length
                    while (i--) {
                        nodeStack.push(node.childNodes[i])
                    }
                }
            }

            range.collapse(true)
            selection.removeAllRanges()
            selection.addRange(range)
        }

        const updateEditor = (index: number, event: Event) => {
            const target = event.target as HTMLInputElement
            const savedPosition = saveCursorPosition(target)

            renderText(index, target)

            if (savedPosition) {
                restoreCursorPosition(target, savedPosition)
            }
        }

        const handleKeydown = (index: number, event: KeyboardEvent) => {
            const editor = document.getElementById(`editor-${index}`)
            if (!editor) return

            const selection = window.getSelection()
            if (!selection) return

            const anchorNode = selection.anchorNode as HTMLElement
            const focusNode = selection.focusNode as HTMLElement

            if (
                anchorNode &&
                focusNode &&
                (anchorNode.parentElement?.classList.contains('non-editable') ||
                    focusNode.parentElement?.classList.contains('non-editable'))
            ) {
                event.preventDefault()
            }
        }

        const setTemplates = () => {
            state.templates.forEach((t, i) => {
                renderText(i)
            })
        }

        //

        const loadNotificationIt = async () => {
            if (!props.id) return

            try {
                state.is_loading = true

                const response = await settings.getNotificationIt(props.id)

                if (response.data) {
                    state.templates = response.data.templates
                    state.templates_copy = [...response.data.templates]

                    state.waba_templates = response.data.templates.filter((t) => t.channel === 'whatsapp').map((t) => t.id)

                    setTimeout(setTemplates, 100)
                }
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении информации произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadNotificationIt()
            loadTemplates()
        })

        const isCanUpdateWabaTemplates = computed(() => {
            if (state.is_loading) return false
            return havePermissions(['notification.company-settings.sync-whatsapp-template', 'all-root-permission'])
        })

        //

        return {
            state,
            data_state,
            state_form$,
            //
            updateNotification,
            closeModal,
            updateEditor,
            handleKeydown,
            loadTemplates,
            updateWabaTemplates,
            //
            isCanUpdateWabaTemplates,
        }
    },
})
